import { Grid } from '../common/Grid';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  alpha,
  useTheme,
  InputAdornment,
  FormControlLabel,
  Switch,
  Skeleton,
} from '@mui/material';
import {
  SwapHoriz,
  Warning,
  Delete,
  CheckCircle,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  ArrowForward,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import orderService, { type RxSwapEquivalence } from '../../services/orderService';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useRoleLabel } from '../../hooks/useRoleLabel';
import { isPharmacistRole } from '../../constants/pharmacistRoles';
import { usePharmacyStore } from '../../stores/pharmacyStore';
import { pmsDisplayName } from '../../utils/pmsDisplayName';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface SwapRxDialogProps {
  open: boolean;
  onClose: () => void;
  order: any;
  pharmacyId: string;
  onSuccess: () => void;
}

/**
 * GP-1380 — per-row state of the clinical-equivalence check that runs the moment
 * a candidate RX is picked from Kroll.
 */
export interface RxEquivalenceState {
  status: 'loading' | 'ready' | 'error';
  result?: RxSwapEquivalence;
  error?: string;
}

/** One selected row, reduced to what the submit gate needs. */
export interface SwapRxGateEntry {
  hasNewRx: boolean;
  equivalence?: RxEquivalenceState;
}

export interface SwapRxGateResult {
  canSubmit: boolean;
  /** Why the button is disabled — surfaced as its tooltip. */
  blockedReason: string | null;
  /** At least one selected RX failed a criterion (or could not be verified). */
  hasFailures: boolean;
  isChecking: boolean;
}

/**
 * GP-1380 — decide whether the swap may be submitted.
 *
 * The Swap RX action rewrites the RX reference on a DELIVERED, insurance-billed
 * order, so a selection is only submittable once every picked RX has been proven
 * clinically equivalent to the one it replaces. A failing (or unverifiable)
 * criterion is a hard block for everyone except a pharmacist, who may override
 * explicitly with a reason — which the server then records in the action log.
 *
 * Exported so the gate is unit-testable without rendering the dialog; the server
 * enforces the same rule independently in `swapRxNumber()`.
 */
export function evaluateSwapRxGate(input: {
  entries: SwapRxGateEntry[];
  isPharmacist: boolean;
  pharmacistOverride: boolean;
  reason: string;
  /** The pharmacy's own system, as `pmsDisplayName` names it ('Kroll' or 'Fillware'). */
  pmsName: string;
}): SwapRxGateResult {
  const { entries, isPharmacist, pharmacistOverride, reason, pmsName } = input;
  const selected = entries.filter((e) => e.hasNewRx);

  const base = { canSubmit: false, hasFailures: false, isChecking: false };

  if (selected.length === 0) {
    return { ...base, blockedReason: `Select a new RX number from ${pmsName} to continue.` };
  }

  if (selected.some((e) => !e.equivalence || e.equivalence.status === 'loading')) {
    return {
      ...base,
      isChecking: true,
      blockedReason: 'Verifying the new RX against the current one…',
    };
  }

  // A check that could not run is NOT a pass — an unverifiable swap stays blocked
  // even for a pharmacist, because there is nothing to override against.
  if (selected.some((e) => e.equivalence!.status === 'error')) {
    return {
      ...base,
      hasFailures: true,
      blockedReason: `The new RX could not be verified against ${pmsName}. Resolve the lookup before swapping.`,
    };
  }

  const failing = selected.filter((e) => !e.equivalence!.result?.allPassed);

  if (failing.length === 0) {
    return { canSubmit: true, blockedReason: null, hasFailures: false, isChecking: false };
  }

  if (!isPharmacist) {
    return {
      ...base,
      hasFailures: true,
      blockedReason:
        'The selected RX is not clinically equivalent. Only a pharmacist can override this swap.',
    };
  }

  if (!pharmacistOverride) {
    return {
      ...base,
      hasFailures: true,
      blockedReason:
        'Confirm the pharmacist override to swap an RX that fails the equivalence check.',
    };
  }

  if (!reason.trim()) {
    return {
      ...base,
      hasFailures: true,
      blockedReason: 'A reason is required when a pharmacist overrides the equivalence check.',
    };
  }

  return { canSubmit: true, blockedReason: null, hasFailures: true, isChecking: false };
}

interface RxSwapEntry {
  oldRxNumber: string;
  medicationName: string;
  newRxNumber: string;
  newRxDetails?: any; // Full Kroll RX details
  quantity?: number;
  daysSupply?: number;
}

interface KrollRx {
  rxNumber: string;
  medicationName: string;
  genericName?: string;
  lastFillDate?: string;
  quantity?: number;
  daysSupply?: number;
  doctorName?: string;
  status?: number;
  din?: string;
  _raw?: any;
}

export const SwapRxDialog: React.FC<SwapRxDialogProps> = ({
  open,
  onClose,
  order,
  pharmacyId,
  onSuccess,
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { customRole } = useAuth();
  const roleLabel = useRoleLabel();
  const isPharmacist = isPharmacistRole(customRole?.customRole);
  // The order's pharmacy names its own system in the copy: Fillware at
  // PharmAssist, Kroll everywhere else (see pmsDisplayName).
  const pmsName = pmsDisplayName(
    usePharmacyStore(
      (s) => s.pharmacies.find((pharmacy) => pharmacy.id === pharmacyId)?.databaseType ?? null,
    ),
  );
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  const [swapEntries, setSwapEntries] = useState<RxSwapEntry[]>([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // GP-1380: clinical-equivalence check per swap row, keyed by row index.
  const [equivalence, setEquivalence] = useState<Record<number, RxEquivalenceState>>({});
  const [pharmacistOverride, setPharmacistOverride] = useState(false);

  // Kroll RX list state
  const [krollRxList, setKrollRxList] = useState<KrollRx[]>([]);
  const [krollLoading, setKrollLoading] = useState(false);
  const [krollError, setKrollError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAllMedications, setShowAllMedications] = useState(false);
  const [showAllFills, setShowAllFills] = useState(true);
  const [selectedForSwapIndex, setSelectedForSwapIndex] = useState<number | null>(null);

  // Initialize swap entries when dialog opens
  useEffect(() => {
    if (open && order) {
      // Get medications from metadata or fallback to order-level RX
      const billingMeds = order.metadata?.billingData?.medications || [];

      if (billingMeds.length > 0) {
        // Multi-medication order
        const entries: RxSwapEntry[] = billingMeds.map((med: any) => ({
          oldRxNumber: String(med.rxNumber || ''),
          medicationName: med.medicationName || 'Unknown',
          newRxNumber: '',
          quantity: med.quantity,
          daysSupply: med.daysSupply,
        }));
        setSwapEntries(entries);
      } else {
        // Single medication order (fallback to order-level fields)
        setSwapEntries([
          {
            oldRxNumber: order.rxNumber || '',
            medicationName: order.medicationName || 'Unknown',
            newRxNumber: '',
            quantity: order.quantity,
            daysSupply: order.daysSupply,
          },
        ]);
      }

      setReason('');
      setErrors({});
      setSelectedForSwapIndex(null);
      setEquivalence({});
      setPharmacistOverride(false);
      setConfirmDiscardOpen(false);

      // Fetch patient's Kroll RX list
      if (order.patientId) {
        fetchKrollRxList(order.patientId);
      }
    }
  }, [open, order]);

  const fetchKrollRxList = async (patientId: string) => {
    setKrollLoading(true);
    setKrollError(null);
    try {
      const filter = showAllMedications ? 'all' : 'active';
      const groupByParent = !showAllFills; // When showAllFills is true, set groupByParent to false

      const response = await api.get(
        `/api/rx/${pharmacyId}/patient/${patientId}/all-prescriptions?filter=${filter}&groupByParent=${groupByParent}`
      );

      if (response.data?.success && response.data?.data) {
        // Map API response to internal format (same mapping as SelectMedicationStep)
        const mappedRx = response.data.data.map((rx: any) => ({
          // Core identification
          rxNumber: String(rx.RxNum),

          // Drug info
          medicationName: rx.BrandName || rx.GenericName || 'Unknown',
          genericName: rx.GenericName,
          brandName: rx.BrandName,
          din: rx.DIN || rx.DrgDIN,

          // Dispensing details
          quantity: rx.DispQty || 0,
          daysSupply: rx.DaysSupply || 0,
          lastFillDate: rx.LastFillDate || rx.FillDate,

          // Status (numeric from Kroll)
          status: rx.Status,

          // Prescriber
          doctorName: rx.DocName || null,

          // Raw Kroll data for reference
          _raw: rx,
        }));

        setKrollRxList(mappedRx);
      } else {
        setKrollError('No prescriptions found for this patient');
      }
    } catch (error: any) {
      console.error('Failed to fetch Kroll RX list:', error);
      setKrollError(error.response?.data?.message || `Failed to load patient prescriptions from ${pmsName}`);
    } finally {
      setKrollLoading(false);
    }
  };

  useEffect(() => {
    if (open && order?.patientId) {
      fetchKrollRxList(order.patientId);
    }
  }, [showAllMedications, showAllFills]);

  if (!order) return null;

  /**
   * GP-1380: verify the picked RX against the one it replaces.
   *
   * The server owns the comparison (same code path the swap endpoint enforces),
   * so a failure here is a genuine block, not a client-side opinion.
   */
  const runEquivalenceCheck = async (swapIndex: number, oldRxNumber: string, newRxNumber: string) => {
    setEquivalence((prev) => ({ ...prev, [swapIndex]: { status: 'loading' } }));
    try {
      const result = await orderService.checkRxSwapEquivalence(
        pharmacyId,
        order.id,
        oldRxNumber,
        newRxNumber,
      );
      setEquivalence((prev) => ({ ...prev, [swapIndex]: { status: 'ready', result } }));
    } catch (error: any) {
      console.error('Failed to verify RX swap equivalence:', error);
      setEquivalence((prev) => ({
        ...prev,
        [swapIndex]: {
          status: 'error',
          error:
            error.response?.data?.message ||
            `Could not verify RX ${newRxNumber} against RX ${oldRxNumber} in ${pmsName}`,
        },
      }));
    }
  };

  const handleSelectKrollRx = (krollRx: KrollRx, swapIndex: number) => {
    setSwapEntries((prev) =>
      prev.map((entry, i) =>
        i === swapIndex
          ? {
              ...entry,
              newRxNumber: krollRx.rxNumber,
              newRxDetails: krollRx,
            }
          : entry,
      ),
    );
    setSelectedForSwapIndex(null); // Close selection mode
    // Clear error for this row
    if (errors[`rx-${swapIndex}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`rx-${swapIndex}`];
        return newErrors;
      });
    }
    // The override is an acknowledgement of THIS pair's specific differences, so
    // changing the pair retracts it — otherwise a pharmacist who accepted RX A's
    // mismatch could ship RX B's without ever seeing it.
    setPharmacistOverride(false);
    void runEquivalenceCheck(swapIndex, swapEntries[swapIndex].oldRxNumber, krollRx.rxNumber);
  };

  const handleRemoveEntry = (index: number) => {
    if (swapEntries.length === 1) {
      enqueueSnackbar('Cannot remove the last medication', { variant: 'warning' });
      return;
    }
    setSwapEntries((prev) => prev.filter((_, i) => i !== index));
    // Verdicts are keyed by row index, so re-key them to the compacted list —
    // otherwise a removed row's result would gate a different medication.
    setEquivalence((prev) => {
      const next: Record<number, RxEquivalenceState> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const i = Number(key);
        if (i < index) next[i] = value;
        else if (i > index) next[i - 1] = value;
      });
      return next;
    });
    setPharmacistOverride(false);
  };

  const handleClearSelection = (index: number) => {
    setSwapEntries((prev) =>
      prev.map((entry, i) =>
        i === index ? { ...entry, newRxNumber: '', newRxDetails: undefined } : entry,
      ),
    );
    setEquivalence((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setPharmacistOverride(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Check if at least one entry has a new RX number
    const hasAnyNewRx = swapEntries.some((entry) => entry.newRxNumber.trim());
    if (!hasAnyNewRx) {
      newErrors.general = 'At least one medication must have a new RX number';
      setErrors(newErrors);
      return false;
    }

    // Validate each entry that has a new RX number
    swapEntries.forEach((entry, index) => {
      if (entry.newRxNumber.trim()) {
        // Check if new RX is same as old RX
        if (entry.newRxNumber.trim() === entry.oldRxNumber.trim()) {
          newErrors[`rx-${index}`] = 'New RX must be different from old RX';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    // GP-1380: never submit a swap the equivalence gate has not cleared. The
    // button is already disabled — this keeps that true for any other caller.
    if (!gate.canSubmit) {
      if (gate.blockedReason) enqueueSnackbar(gate.blockedReason, { variant: 'warning' });
      return;
    }

    // Filter entries that have new RX numbers
    const entriesToSwap = swapEntries.filter((entry) => entry.newRxNumber.trim());

    if (entriesToSwap.length === 0) {
      return;
    }

    try {
      setLoading(true);

      // If single RX swap (most common case)
      if (entriesToSwap.length === 1) {
        const entry = entriesToSwap[0];
        const payload: any = {
          newRxNumber: entry.newRxNumber.trim(),
          oldRxNumber: entry.oldRxNumber.trim(),
          reason: reason.trim() || undefined,
          // Only sent when a criterion actually failed; the server ignores it
          // otherwise and rejects it outright for non-pharmacists.
          pharmacistOverride: pharmacistOverride || undefined,
        };

        const response = await orderService.swapRxNumber(pharmacyId, order.id, payload);

        if (response.success) {
          // GP-1433: say what happened to the money, not just to the RX number.
          // A failed Kroll reprice is a warning, not a success — the copay on
          // screen is then still the pre-swap one and needs checking by hand.
          const repricing = response.repricing;
          const notRepricedText = (() => {
            switch (repricing?.reasonCode) {
              case 'KROLL_PRICE_MISSING':
                // A NULL patient price is not a $0 copay — nothing was written.
                return `. ${pmsName} has no patient price for RX ${response.newRxNumber} — the copay was NOT changed; check it manually.`;
              case 'CC_CHILD_CONSOLIDATED':
                return `. This is the cold-chain half of a split; its copay lives on the parent order — check the parent's copay against the rebilled RX.`;
              case 'CC_PARENT_OLD_PRICE_UNKNOWN':
                return `. The order-level copay was NOT changed — the swapped medication had no pre-swap price to re-derive it from; check it manually.`;
              default:
                return `. The copay could not be re-pulled from ${pmsName} — check it manually.`;
            }
          })();
          enqueueSnackbar(
            `RX number updated from ${response.previousRxNumber} to ${response.newRxNumber}` +
              (repricing?.applied
                ? `. Copay is now $${repricing.newCopayAmount.toFixed(2)}` +
                  (repricing.outstandingBalance > 0
                    ? ` ($${repricing.outstandingBalance.toFixed(2)} outstanding).`
                    : repricing.overpaidBy > 0
                      ? `, overpaid by $${repricing.overpaidBy.toFixed(2)} — issue a refund.`
                      : '.')
                : notRepricedText),
            { variant: repricing?.applied ? 'success' : 'warning' },
          );
          onSuccess();
          onClose();
        }
      } else {
        // Multiple RX swaps - process sequentially
        let successCount = 0;
        const failures: string[] = [];
        for (const entry of entriesToSwap) {
          try {
            await orderService.swapRxNumber(pharmacyId, order.id, {
              newRxNumber: entry.newRxNumber.trim(),
              oldRxNumber: entry.oldRxNumber.trim(),
              reason: reason.trim() || undefined,
              pharmacistOverride: pharmacistOverride || undefined,
            });
            successCount++;
          } catch (err: any) {
            console.error(`Failed to swap RX ${entry.oldRxNumber}:`, err);
            failures.push(
              `RX ${entry.oldRxNumber}: ${err.response?.data?.message || 'swap failed'}`,
            );
          }
        }

        // Name the ones that did NOT swap. The server now rejects a
        // non-equivalent swap per RX, so a partial failure is a real outcome —
        // and an RX left pointing at the old Kroll claim has to be visible, not
        // buried in a "1 of 2" count.
        if (failures.length > 0) {
          enqueueSnackbar(failures.join(' · '), { variant: 'error', autoHideDuration: 10000 });
        }

        if (successCount > 0) {
          enqueueSnackbar(
            `Successfully swapped ${successCount} of ${entriesToSwap.length} RX numbers`,
            { variant: 'success' },
          );
          onSuccess();
          onClose();
        } else {
          throw new Error('Failed to swap any RX numbers');
        }
      }
    } catch (err: any) {
      console.error('Error swapping RX numbers:', err);
      const errorMessage = err.response?.data?.message || 'Failed to swap RX numbers';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    const hasChanges = swapEntries.some((entry) => entry.newRxNumber.trim());
    if (hasChanges) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  };

  const completedCount = swapEntries.filter((entry) => entry.newRxNumber.trim()).length;

  // GP-1380: nothing is submittable until every picked RX has been proven
  // clinically equivalent (or a pharmacist has explicitly overridden).
  const gate = evaluateSwapRxGate({
    entries: swapEntries.map((entry, index) => ({
      hasNewRx: !!entry.newRxNumber.trim(),
      equivalence: equivalence[index],
    })),
    isPharmacist,
    pharmacistOverride,
    reason,
    pmsName,
  });

  const reasonRequired = gate.hasFailures && isPharmacist && pharmacistOverride;

  const filteredKrollRx = krollRxList.filter((rx) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      rx.rxNumber?.includes(search) ||
      rx.medicationName?.toLowerCase().includes(search) ||
      rx.genericName?.toLowerCase().includes(search) ||
      rx.din?.includes(search)
    );
  });

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return '—';
    }
  };

  const getStatusLabel = (status: number | undefined): string => {
    if (status === undefined || status === null) return 'Unknown';
    const statusMap: Record<number, string> = {
      1: 'Active',
      2: 'Cancelled',
      3: 'Unfilled',
      4: 'Not Dispensed',
      5: 'Deleted',
      6: 'On File',
      7: 'Exp. Svc (Cancelled)',
      8: 'Suspended',
    };
    return statusMap[status] || `Unknown (${status})`;
  };

  const getStatusColor = (status: number | undefined): 'success' | 'warning' | 'error' | 'default' => {
    if (status === undefined || status === null) return 'default';
    if ([1, 6, 8].includes(status)) return 'success';
    if ([3, 4].includes(status)) return 'warning';
    if ([2, 5, 7].includes(status)) return 'error';
    return 'default';
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapHoriz color="warning" />
          <span>Swap RX Numbers</span>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Order Summary */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Order
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                #{order.orderNumber || order.id?.slice(-8)}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Patient
              </Typography>
              <Typography variant="body1">{order.patientName}</Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="subtitle2" color="text.secondary">
                Patient ID
              </Typography>
              <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                {order.patientId}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Warning Alert */}
        <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
          This will update RX references on a completed order. Only perform this after rebilling in
          {' '}{pmsName}.
        </Alert>

        {/* GP-1433: state what the swap does to the money. It used to leave the
            pre-swap pricing in place silently, so an order sat at "$0.00 /
            Pending" for a real copay with nothing on screen to explain it. */}
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{ mb: 3 }}
          data-testid="swap-rx-repricing-notice"
        >
          The order's copay and insurance amounts are re-pulled from {pmsName} for the new RX. Any
          payment already collected stays on the order and is credited against the rebilled copay.
        </Alert>

        {errors.general && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errors.general}
          </Alert>
        )}

        {/* Progress Indicator */}
        {completedCount > 0 && (
          <Box sx={{ mb: 2 }}>
            <Chip
              label={`${completedCount} of ${swapEntries.length} medications ready to swap`}
              color="primary"
              size="small"
              icon={<CheckCircle />}
            />
          </Box>
        )}

        {/* Current Medications - Select New RX */}
        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
          Step 1: Select New RX Numbers from {pmsName}
        </Typography>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            mb: 3,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                <TableCell sx={{ fontWeight: 700 }}>Old RX #</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Medication</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">
                  Days
                </TableCell>
                <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>New RX # (from {pmsName})</TableCell>
                <TableCell sx={{ fontWeight: 700, width: 120 }} align="center">
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {swapEntries.map((entry, index) => (
                <TableRow key={`${entry.oldRxNumber}-${index}`} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {entry.oldRxNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{entry.medicationName}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{entry.quantity || '—'}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{entry.daysSupply || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {entry.newRxNumber ? (
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            label={`Rx: ${entry.newRxNumber}`}
                            color="success"
                            size="small"
                            icon={<CheckCircle />}
                          />
                          {entry.newRxDetails && (
                            <Typography variant="caption" color="text.secondary">
                              {entry.newRxDetails.medicationName}
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => setSelectedForSwapIndex(index)}
                        startIcon={<SearchIcon />}
                      >
                        Select from {pmsName}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      {entry.newRxNumber && (
                        <Tooltip title="Clear selection">
                          <IconButton
                            size="small"
                            onClick={() => handleClearSelection(index)}
                            disabled={loading}
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      {swapEntries.length > 1 && (
                        <Tooltip title="Remove medication from swap">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveEntry(index)}
                            disabled={loading || swapEntries.length === 1}
                            color="error"
                          >
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/*
          GP-1380: side-by-side clinical-equivalence comparison.
          Rendered the moment an RX is picked so the operator can SEE the DIN,
          quantity, day supply, directions and originating prescription they are
          swapping — the values the old modal never showed at all.
        */}
        {swapEntries.map((entry, index) => {
          if (!entry.newRxNumber.trim()) return null;
          const state = equivalence[index];

          return (
            <Box key={`equivalence-${entry.oldRxNumber}-${index}`} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Equivalence check: RX {entry.oldRxNumber} → RX {entry.newRxNumber}
              </Typography>

              {(!state || state.status === 'loading') && (
                <Box display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Comparing the new RX against the current one in {pmsName}…
                  </Typography>
                </Box>
              )}

              {state?.status === 'error' && (
                <Alert severity="error">
                  {state.error} — this swap is blocked until the RX can be verified.
                </Alert>
              )}

              {state?.status === 'ready' && state.result && (
                <>
                  <Alert
                    severity={state.result.allPassed ? 'success' : 'error'}
                    icon={state.result.allPassed ? <CheckCircle /> : <CancelIcon />}
                    sx={{ mb: 1 }}
                  >
                    {state.result.allPassed
                      ? 'All 6 criteria match — this RX is clinically equivalent.'
                      : `Not clinically equivalent — ${state.result.failedLabels}.`}
                  </Alert>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: '1px solid', borderColor: 'divider' }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: alpha(theme.palette.info.main, 0.08) }}>
                          <TableCell sx={{ fontWeight: 700 }}>Criteria</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            Old RX {entry.oldRxNumber}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            New RX {entry.newRxNumber}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, width: 120 }} align="center">
                            Result
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {state.result.checks.map((check) => (
                          <TableRow
                            key={check.criterion}
                            sx={{
                              bgcolor: check.passed
                                ? 'transparent'
                                : alpha(theme.palette.error.main, 0.06),
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {check.label}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{check.oldValue}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={check.passed ? 400 : 700}>
                                {check.newValue}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                color={check.passed ? 'success' : 'error'}
                                icon={check.passed ? <CheckCircle /> : <CancelIcon />}
                                label={
                                  check.passed
                                    ? 'Match'
                                    : check.unverified
                                      ? `Not in ${pmsName}`
                                      : 'Mismatch'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          );
        })}

        {/* Kroll RX Selection Table */}
        {selectedForSwapIndex !== null && (
          <>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" gutterBottom>
              Select New RX for: {swapEntries[selectedForSwapIndex].medicationName}
            </Typography>

            {krollError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {krollError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search prescriptions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1, maxWidth: 400 }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllMedications}
                    onChange={(e) => setShowAllMedications(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show inactive
                  </Typography>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showAllFills}
                    onChange={(e) => setShowAllFills(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Show all fills
                  </Typography>
                }
              />
            </Box>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                maxHeight: '400px',
                overflowY: 'auto',
                mb: 2,
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      RxNum
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Drug Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                      Fill Date
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      align="center"
                    >
                      Qty
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      align="center"
                    >
                      Days
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                      align="center"
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {krollLoading ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ ml: 2 }}>
                            Loading prescriptions from {pmsName}...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : filteredKrollRx.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <Typography variant="body2" color="text.secondary" align="center" py={4}>
                          No prescriptions found. Try adjusting the filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredKrollRx.map((krollRx) => {
                      // Check if this RX is already used in current swap entries
                      const isAlreadySelected = swapEntries.some(
                        (e, i) => i !== selectedForSwapIndex && e.newRxNumber === krollRx.rxNumber,
                      );
                      // Check if this is the OLD RX we're trying to replace
                      const isOldRx = swapEntries[selectedForSwapIndex].oldRxNumber === krollRx.rxNumber;

                      return (
                        <TableRow
                          key={krollRx.rxNumber}
                          hover
                          sx={{
                            cursor: isAlreadySelected || isOldRx ? 'not-allowed' : 'pointer',
                            opacity: isAlreadySelected || isOldRx ? 0.5 : 1,
                          }}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                            >
                              {krollRx.rxNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{krollRx.medicationName}</Typography>
                            {krollRx.genericName && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {krollRx.genericName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(krollRx.status)}
                              size="small"
                              color={getStatusColor(krollRx.status)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{formatDate(krollRx.lastFillDate)}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{krollRx.quantity || '—'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body2">{krollRx.daysSupply || '—'}</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip
                              title={
                                isOldRx
                                  ? 'This is the current RX'
                                  : isAlreadySelected
                                    ? 'Already selected for another medication'
                                    : 'Select this RX'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="primary"
                                  onClick={() =>
                                    handleSelectKrollRx(krollRx, selectedForSwapIndex)
                                  }
                                  disabled={isAlreadySelected || isOldRx}
                                  startIcon={<ArrowForward />}
                                >
                                  Select
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Button
              variant="outlined"
              onClick={() => setSelectedForSwapIndex(null)}
              sx={{ mb: 2 }}
            >
              Cancel Selection
            </Button>
          </>
        )}

        {/* Reason — REQUIRED once a pharmacist overrides a failed criterion (GP-1380) */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" gutterBottom>
          Step 2: Add Reason {reasonRequired ? '(Required)' : '(Optional)'}
        </Typography>
        <TextField
          label={reasonRequired ? 'Reason (Required)' : 'Reason (Optional)'}
          fullWidth
          required={reasonRequired}
          error={reasonRequired && !reason.trim()}
          multiline
          rows={2}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`e.g., Retroactive insurance coverage - rebilled in ${pmsName}`}
          disabled={loading}
          helperText={
            reasonRequired
              ? 'Recorded in the order action log alongside the failed criteria'
              : 'Optional: Provide a reason for the RX swap(s)'
          }
        />

        {/*
          GP-1380: a failing criterion hard-blocks a pharmacy assistant. Only a
          pharmacist can push it through, and only deliberately — the override
          plus the failed criteria land in the order action log, which is what
          replaces per-claim pharmacist review of an equivalent swap.
        */}
        {gate.hasFailures && (
          <Box sx={{ mt: 2 }}>
            {isPharmacist ? (
              <>
                <FormControlLabel
                  control={
                    <Switch
                      checked={pharmacistOverride}
                      onChange={(e) => setPharmacistOverride(e.target.checked)}
                      color="error"
                      disabled={loading}
                    />
                  }
                  label={
                    <Typography variant="body2">
                      Pharmacist override — I have reviewed this claim and accept the differences
                    </Typography>
                  }
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  Signed in as {roleLabel(customRole?.customRole)}. The override, the
                  failed criteria and your reason are written to the order action log.
                </Typography>
              </>
            ) : (
              <Alert severity="warning" icon={<InfoIcon />}>
                This swap fails the equivalence check. Only a pharmacist can review and override
                it — ask a pharmacist to complete the swap, or pick a matching RX.
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Tooltip title={gate.blockedReason || ''}>
          <span>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="warning"
              disabled={loading || !gate.canSubmit}
              startIcon={loading ? <CircularProgress size={16} /> : <SwapHoriz />}
            >
              {loading
                ? 'Swapping...'
                : `Swap ${completedCount > 0 ? completedCount : ''} RX Number${completedCount !== 1 ? 's' : ''}`}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Discard unsaved changes?"
        message="The RX numbers you picked have not been swapped. Closing discards them."
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        destructive
        onConfirm={() => {
          setConfirmDiscardOpen(false);
          onClose();
        }}
        onClose={() => setConfirmDiscardOpen(false)}
      />
    </Dialog>
  );
};
